import React, { useState } from 'react';
import { Layers } from 'lucide-react';
import {
    TAGLEVELS, microTestLabel,
    BCS_MODEL_TEST_DISTRIBUTION, BCS_MODEL_TEST_BASE_TOTAL, BCS_MINUTES_PER_QUESTION, BCS_COMPETITION_TOTAL,
    MEDICAL_MODEL_TEST_DISTRIBUTION, MEDICAL_MODEL_TEST_BASE_TOTAL, MEDICAL_MINUTES_PER_QUESTION,
    DU_MODEL_TEST_DISTRIBUTION, DU_MODEL_TEST_BASE_TOTAL, DU_MINUTES_PER_QUESTION, DU_COMPETITION_TOTAL,
} from '../utils/Constants';
import useLocalStorageLevel from '../contexts/levelHook';
import { useApiCaller } from '../utils/ApiUtilities';
import {
    getGreGeneratePublishedUrl, getGreGenerateMicroUrl,
    getSatGeneratePublishedUrl, getSatGenerateMicroUrl,
    getBcsGenerateMicroUrl, getBcsGeneratePublishedUrl, getBcsGenerateCompetitionUrl,
    getMedicalGenerateMicroUrl, getMedicalGeneratePublishedUrl, getMedicalGenerateCompetitionUrl,
    getDuGenerateMicroUrl, getDuGeneratePublishedUrl, getDuGenerateCompetitionUrl,
} from '../utils/UrlUtilities';
import GenerateMicroQuizModal from './GenerateMicroQuizModal';
import MicroQuizReportModal from './MicroQuizReportModal';
import GenerateCompetitionModal from './GenerateCompetitionModal';
import CompetitionResultModal from './CompetitionResultModal';
import GreGenerateReportModal from './GreGenerateReportModal';
import GreCompetitionModal from './GreCompetitionModal';
import SatCompetitionModal from './SatCompetitionModal';
import ModelTestBuilderModal from './ModelTestBuilderModal';
import CompetitionBuilderModal from './CompetitionBuilderModal';

// Admin "Auto Quiz Build" page: one tab per exam level. Model-test levels (TOEFL, IELTS) split a
// hand-authored model test into per-section micro quizzes + merge micros into competitions. GRE
// instead GENERATES model tests / micros from the question bank. Other tabs are placeholders.
const FUNCTIONAL_LEVELS = ['TOEFL', 'IELTS'];

const PURPLE_GRADIENT = 'linear-gradient(145deg,#7c3aed,#5b21b6)';

// 3D pill tab styling in the site's purple/lavender theme.
const tabStyle = (isActive, isHovered) => {
    const base = {
        border: 'none',
        borderRadius: '0.7rem',
        padding: '0.55rem 1.15rem',
        fontWeight: 700,
        fontSize: '0.95rem',
        cursor: 'pointer',
        transition: 'transform 0.12s ease, box-shadow 0.12s ease, background 0.12s ease',
        outline: 'none',
    };
    if (isActive) {
        return {
            ...base,
            background: PURPLE_GRADIENT,
            color: '#ffffff',
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 16px -5px rgba(124,58,237,0.55), inset 0 1px 0 rgba(255,255,255,0.35)',
        };
    }
    return {
        ...base,
        background: isHovered ? 'linear-gradient(145deg,#ede9fe,#ddd6fe)' : 'linear-gradient(145deg,#faf5ff,#ede9fe)',
        color: '#5b21b6',
        transform: isHovered ? 'translateY(-2px)' : 'translateY(0)',
        boxShadow: isHovered
            ? '0 6px 14px -6px rgba(91,33,182,0.45), inset 0 1px 0 rgba(255,255,255,0.8)'
            : '0 2px 5px -2px rgba(91,33,182,0.3), inset 0 1px 0 rgba(255,255,255,0.8)',
    };
};

function AutoQuizBuild() {
    const [storedLevel] = useLocalStorageLevel();
    const CallApi = useApiCaller();
    // Default the active tab to the admin's currently selected level. A submenu leaf (e.g.
    // VARSITY-DU) maps to its parent tab (VARSITY); anything unknown falls back to TOEFL.
    const initialTab = (() => {
        if (TAGLEVELS.some(t => t.value === storedLevel)) return storedLevel;
        const parent = TAGLEVELS.find(t => (t.submenu || []).some(s => s.value === storedLevel));
        return parent?.value || 'TOEFL';
    })();
    const [activeLevel, setActiveLevel] = useState(initialTab);
    const [showModal, setShowModal] = useState(false);
    const [report, setReport] = useState(null);
    const [hoveredTab, setHoveredTab] = useState(null);
    const [showCompetition, setShowCompetition] = useState(false);
    const [competitionResult, setCompetitionResult] = useState(null);
    // GRE generation state.
    const [greBusy, setGreBusy] = useState('');            // 'published' | 'micro' | ''
    const [greReport, setGreReport] = useState(null);      // { report, kind }
    const [showGreCompetition, setShowGreCompetition] = useState(false);
    // SAT generation state (report reuses greReport/GreGenerateReportModal).
    const [satBusy, setSatBusy] = useState('');            // 'published' | 'micro' | ''
    const [showSatCompetition, setShowSatCompetition] = useState(false);
    // BCS generation state (reports reuse greReport/GreGenerateReportModal).
    const [bcsBusy, setBcsBusy] = useState('');            // 'micro' | ''
    const [showBcsFull, setShowBcsFull] = useState(false);
    const [showBcsCompetition, setShowBcsCompetition] = useState(false);
    // Medical generation state (reports reuse greReport/GreGenerateReportModal).
    const [medicalBusy, setMedicalBusy] = useState('');    // 'micro' | ''
    const [showMedicalFull, setShowMedicalFull] = useState(false);
    const [showMedicalCompetition, setShowMedicalCompetition] = useState(false);
    // DU A-Unit generation state (VARSITY tab; reports reuse greReport/GreGenerateReportModal).
    const [duBusy, setDuBusy] = useState('');              // 'micro' | ''
    const [showDuFull, setShowDuFull] = useState(false);
    const [showDuCompetition, setShowDuCompetition] = useState(false);

    // Run a one-click GRE generation (published or micro) and surface the report.
    const runGre = async (kind) => {
        if (greBusy) return;
        const label = kind === 'published' ? 'published model tests' : 'micro tests';
        if (!window.confirm(`Generate as many GRE ${label} as the question bank allows? New tests are created unpublished.`)) return;
        setGreBusy(kind);
        try {
            const url = kind === 'published' ? getGreGeneratePublishedUrl() : getGreGenerateMicroUrl();
            const res = await CallApi({ url, method: 'POST', payload: {}, token: true });
            if (res.success) {
                setGreReport({ report: res.data, kind: kind === 'published' ? 'Published' : 'Micro' });
            }
        } finally {
            setGreBusy('');
        }
    };

    // Run a one-click SAT generation (published model tests or topic-wise micros).
    const runSat = async (kind) => {
        if (satBusy) return;
        const label = kind === 'published' ? 'full published model tests' : 'topic-wise model tests';
        if (!window.confirm(`Generate as many SAT ${label} as the question bank allows? New tests are created unpublished.`)) return;
        setSatBusy(kind);
        try {
            const url = kind === 'published' ? getSatGeneratePublishedUrl() : getSatGenerateMicroUrl();
            const res = await CallApi({ url, method: 'POST', payload: {}, token: true });
            if (res.success) {
                setGreReport({ report: res.data, kind: kind === 'published' ? 'Published' : 'Micro' });
            }
        } finally {
            setSatBusy('');
        }
    };

    // Run the one-click BCS topic-wise micro generation.
    const runBcs = async (kind) => {
        if (bcsBusy) return;
        if (!window.confirm('Generate a topic-wise BCS model test (≤20 questions per topic) for every topic the bank allows? New tests are created unpublished.')) return;
        setBcsBusy(kind);
        try {
            const res = await CallApi({ url: getBcsGenerateMicroUrl(), method: 'POST', payload: {}, token: true });
            if (res.success) {
                setGreReport({ report: res.data, kind: 'Micro' });
            }
        } finally {
            setBcsBusy('');
        }
    };

    // Run the one-click Medical topic-wise micro generation.
    const runMedical = async (kind) => {
        if (medicalBusy) return;
        if (!window.confirm('Generate a topic-wise Medical model test (≤20 questions per topic) for every topic the bank allows? New tests are created unpublished.')) return;
        setMedicalBusy(kind);
        try {
            const res = await CallApi({ url: getMedicalGenerateMicroUrl(), method: 'POST', payload: {}, token: true });
            if (res.success) {
                setGreReport({ report: res.data, kind: 'Micro' });
            }
        } finally {
            setMedicalBusy('');
        }
    };

    // Run the one-click DU A-Unit topic-wise micro generation.
    const runDu = async (kind) => {
        if (duBusy) return;
        if (!window.confirm('Generate a topic-wise DU A-Unit model test (≤20 questions per topic) for every topic the bank allows? New tests are created unpublished.')) return;
        setDuBusy(kind);
        try {
            const res = await CallApi({ url: getDuGenerateMicroUrl(), method: 'POST', payload: {}, token: true });
            if (res.success) {
                setGreReport({ report: res.data, kind: 'Micro' });
            }
        } finally {
            setDuBusy('');
        }
    };

    return (
        <div className="container py-4">
            <h1 className="h3 fw-bold mb-3 d-flex align-items-center gap-2" style={{ color: '#4c1d95' }}>
                <Layers style={{ width: '1.6rem', height: '1.6rem', color: '#7c3aed' }} /> Auto Test Build
            </h1>

            {/* Level tabs — 3D themed pills */}
            <div
                className="d-flex flex-wrap gap-2 mb-3 p-2"
                style={{
                    background: 'linear-gradient(145deg,#f5f3ff,#efeafe)',
                    borderRadius: '0.9rem',
                    boxShadow: 'inset 0 1px 3px rgba(91,33,182,0.12)',
                }}
            >
                {TAGLEVELS.map(lvl => (
                    <button
                        key={lvl.value}
                        type="button"
                        onClick={() => setActiveLevel(lvl.value)}
                        onMouseEnter={() => setHoveredTab(lvl.value)}
                        onMouseLeave={() => setHoveredTab(null)}
                        style={tabStyle(activeLevel === lvl.value, hoveredTab === lvl.value)}
                    >
                        {lvl.label}
                    </button>
                ))}
            </div>

            {/* Tab body */}
            <div
                style={{
                    background: '#ffffff',
                    border: '1px solid #ede9fe',
                    borderRadius: '0.9rem',
                    padding: '1.5rem',
                    boxShadow: '0 10px 24px -12px rgba(91,33,182,0.35)',
                }}
            >
                {FUNCTIONAL_LEVELS.includes(activeLevel) ? (
                    <div>
                        <p className="text-muted">
                            Split a published {activeLevel} model test into per-section micro tests
                            (Reading / Writing / Listening / Speaking), or generate a Reading + Listening
                            competition. New tests are created unpublished.
                        </p>
                        <button
                            className="btn"
                            onClick={() => setShowModal(true)}
                            style={{ background: PURPLE_GRADIENT, color: '#fff', fontWeight: 600, boxShadow: '0 6px 14px -6px rgba(124,58,237,0.6)' }}
                        >
                            Generate {microTestLabel(activeLevel)}
                        </button>
                        <button
                            className="btn ms-2"
                            onClick={() => setShowCompetition(true)}
                            style={{ background: 'linear-gradient(145deg,#f59e0b,#b45309)', color: '#fff', fontWeight: 600, boxShadow: '0 6px 14px -6px rgba(180,83,9,0.6)' }}
                        >
                            Generate Competition Test
                        </button>
                    </div>
                ) : activeLevel === 'GRE' ? (
                    <div>
                        <p className="text-muted">
                            Generate GRE tests from the question bank. <strong>Published</strong> builds as many
                            full model tests (Analytical Writing + 2 Verbal + 2 Quant sections) as the bank allows;
                            <strong> Micro</strong> builds per-topic tests (≤20 questions each);
                            <strong> Competition</strong> assembles Quant + Verbal + Quant from published model tests.
                            New tests are created unpublished.
                        </p>
                        <button
                            className="btn"
                            onClick={() => runGre('published')}
                            disabled={!!greBusy}
                            style={{ background: PURPLE_GRADIENT, color: '#fff', fontWeight: 600, boxShadow: '0 6px 14px -6px rgba(124,58,237,0.6)' }}
                        >
                            {greBusy === 'published' ? 'Generating…' : 'Generate Full Published Model Tests'}
                        </button>
                        <button
                            className="btn ms-2"
                            onClick={() => runGre('micro')}
                            disabled={!!greBusy}
                            style={{ background: 'linear-gradient(145deg,#0ea5e9,#0369a1)', color: '#fff', fontWeight: 600, boxShadow: '0 6px 14px -6px rgba(3,105,161,0.6)' }}
                        >
                            {greBusy === 'micro' ? 'Generating…' : `Generate ${microTestLabel('GRE')}`}
                        </button>
                        <button
                            className="btn ms-2"
                            onClick={() => setShowGreCompetition(true)}
                            disabled={!!greBusy}
                            style={{ background: 'linear-gradient(145deg,#f59e0b,#b45309)', color: '#fff', fontWeight: 600, boxShadow: '0 6px 14px -6px rgba(180,83,9,0.6)' }}
                        >
                            Generate Competition Test
                        </button>
                    </div>
                ) : activeLevel === 'SAT' ? (
                    <div>
                        <p className="text-muted">
                            Generate SAT Digital tests from the question bank. <strong>Published</strong> builds as
                            many full model tests (Reading and Writing 2×27 + Math 2×22, per-module timers) as the
                            bank allows; <strong>Topic-wise</strong> builds per-topic tests (≤20 questions each);
                            <strong> Competition</strong> assembles one Reading and Writing + one Math module from
                            published model tests. New tests are created unpublished.
                        </p>
                        <button
                            className="btn"
                            onClick={() => runSat('published')}
                            disabled={!!satBusy}
                            style={{ background: PURPLE_GRADIENT, color: '#fff', fontWeight: 600, boxShadow: '0 6px 14px -6px rgba(124,58,237,0.6)' }}
                        >
                            {satBusy === 'published' ? 'Generating…' : 'Generate Full Published Model Tests'}
                        </button>
                        <button
                            className="btn ms-2"
                            onClick={() => runSat('micro')}
                            disabled={!!satBusy}
                            style={{ background: 'linear-gradient(145deg,#0ea5e9,#0369a1)', color: '#fff', fontWeight: 600, boxShadow: '0 6px 14px -6px rgba(3,105,161,0.6)' }}
                        >
                            {satBusy === 'micro' ? 'Generating…' : 'Generate Topic-wise Model Tests'}
                        </button>
                        <button
                            className="btn ms-2"
                            onClick={() => setShowSatCompetition(true)}
                            disabled={!!satBusy}
                            style={{ background: 'linear-gradient(145deg,#f59e0b,#b45309)', color: '#fff', fontWeight: 600, boxShadow: '0 6px 14px -6px rgba(180,83,9,0.6)' }}
                        >
                            Generate Competition Test
                        </button>
                    </div>
                ) : activeLevel === 'BCS' ? (
                    <div>
                        <p className="text-muted">
                            Generate BCS tests from the question bank. <strong>Full Model Test</strong> builds one test
                            from a customizable per-subject distribution (default 200 questions, 0.6 min/question), with
                            no question reused across published model tests; <strong>Topic-wise</strong> builds one micro
                            test per topic (≤20 questions each); <strong>Competition</strong> draws 100 questions (an
                            equal share per subject) timed 60 minutes. New tests are created unpublished.
                        </p>
                        <button
                            className="btn"
                            onClick={() => setShowBcsFull(true)}
                            disabled={!!bcsBusy}
                            style={{ background: PURPLE_GRADIENT, color: '#fff', fontWeight: 600, boxShadow: '0 6px 14px -6px rgba(124,58,237,0.6)' }}
                        >
                            Generate Full Published Model Test
                        </button>
                        <button
                            className="btn ms-2"
                            onClick={() => runBcs('micro')}
                            disabled={!!bcsBusy}
                            style={{ background: 'linear-gradient(145deg,#0ea5e9,#0369a1)', color: '#fff', fontWeight: 600, boxShadow: '0 6px 14px -6px rgba(3,105,161,0.6)' }}
                        >
                            {bcsBusy === 'micro' ? 'Generating…' : 'Generate Topic-wise Model Test'}
                        </button>
                        <button
                            className="btn ms-2"
                            onClick={() => setShowBcsCompetition(true)}
                            disabled={!!bcsBusy}
                            style={{ background: 'linear-gradient(145deg,#f59e0b,#b45309)', color: '#fff', fontWeight: 600, boxShadow: '0 6px 14px -6px rgba(180,83,9,0.6)' }}
                        >
                            Generate Competition Test
                        </button>
                    </div>
                ) : activeLevel === 'MEDICAL' ? (
                    <div>
                        <p className="text-muted">
                            Generate Medical Admission tests from the question bank. <strong>Full Model Test</strong>
                            builds one test from a customizable per-subject distribution (default 100 questions —
                            Biology 30, Chemistry 25, Physics 15, English 15, GK 15 — at 0.75 min/question = 75 min),
                            with no question reused across published model tests; <strong>Topic-wise</strong> builds one
                            micro test per topic (≤20 questions each); <strong>Competition</strong> draws the exam
                            distribution (~100 questions) timed 75 minutes. New tests are created unpublished.
                        </p>
                        <button
                            className="btn"
                            onClick={() => setShowMedicalFull(true)}
                            disabled={!!medicalBusy}
                            style={{ background: PURPLE_GRADIENT, color: '#fff', fontWeight: 600, boxShadow: '0 6px 14px -6px rgba(124,58,237,0.6)' }}
                        >
                            Generate Full Published Model Test
                        </button>
                        <button
                            className="btn ms-2"
                            onClick={() => runMedical('micro')}
                            disabled={!!medicalBusy}
                            style={{ background: 'linear-gradient(145deg,#0ea5e9,#0369a1)', color: '#fff', fontWeight: 600, boxShadow: '0 6px 14px -6px rgba(3,105,161,0.6)' }}
                        >
                            {medicalBusy === 'micro' ? 'Generating…' : 'Generate Topic-wise Model Test'}
                        </button>
                        <button
                            className="btn ms-2"
                            onClick={() => setShowMedicalCompetition(true)}
                            disabled={!!medicalBusy}
                            style={{ background: 'linear-gradient(145deg,#f59e0b,#b45309)', color: '#fff', fontWeight: 600, boxShadow: '0 6px 14px -6px rgba(180,83,9,0.6)' }}
                        >
                            Generate Competition Test
                        </button>
                    </div>
                ) : activeLevel === 'VARSITY' ? (
                    <div>
                        <p className="text-muted">
                            Generate <strong>Dhaka University — A Unit</strong> tests from the question bank.
                            <strong> Full Model Test</strong> builds one test from a customizable per-subject
                            distribution (default 60 questions — Physics/Chemistry/Higher Mathematics/Biology, 15
                            each — at 0.75 min/question = 45 min), no question reused across published model tests;
                            <strong> Topic-wise</strong> builds one micro test per topic (≤20 questions each);
                            <strong> Competition</strong> draws the 4 science subjects (~60 questions) timed 45
                            minutes. New tests are created unpublished.
                        </p>
                        <button
                            className="btn"
                            onClick={() => setShowDuFull(true)}
                            disabled={!!duBusy}
                            style={{ background: PURPLE_GRADIENT, color: '#fff', fontWeight: 600, boxShadow: '0 6px 14px -6px rgba(124,58,237,0.6)' }}
                        >
                            Generate Full Published Model Test
                        </button>
                        <button
                            className="btn ms-2"
                            onClick={() => runDu('micro')}
                            disabled={!!duBusy}
                            style={{ background: 'linear-gradient(145deg,#0ea5e9,#0369a1)', color: '#fff', fontWeight: 600, boxShadow: '0 6px 14px -6px rgba(3,105,161,0.6)' }}
                        >
                            {duBusy === 'micro' ? 'Generating…' : 'Generate Topic-wise Model Test'}
                        </button>
                        <button
                            className="btn ms-2"
                            onClick={() => setShowDuCompetition(true)}
                            disabled={!!duBusy}
                            style={{ background: 'linear-gradient(145deg,#f59e0b,#b45309)', color: '#fff', fontWeight: 600, boxShadow: '0 6px 14px -6px rgba(180,83,9,0.6)' }}
                        >
                            Generate Competition Test
                        </button>
                    </div>
                ) : (
                    <div className="text-center text-muted py-5">
                        <p className="mb-1 fw-semibold" style={{ color: '#5b21b6' }}>{activeLevel}</p>
                        <p className="mb-0">Coming soon.</p>
                    </div>
                )}
            </div>

            {showModal && FUNCTIONAL_LEVELS.includes(activeLevel) && (
                <GenerateMicroQuizModal
                    level={activeLevel}
                    onClose={() => setShowModal(false)}
                    onGenerated={(r) => { setShowModal(false); setReport(r); }}
                />
            )}

            {report && (
                <MicroQuizReportModal report={report} onClose={() => setReport(null)} />
            )}

            {showCompetition && FUNCTIONAL_LEVELS.includes(activeLevel) && (
                <GenerateCompetitionModal
                    level={activeLevel}
                    onClose={() => setShowCompetition(false)}
                    onGenerated={(r) => { setShowCompetition(false); setCompetitionResult(r); }}
                />
            )}

            {competitionResult && (
                <CompetitionResultModal result={competitionResult} onClose={() => setCompetitionResult(null)} />
            )}

            {greReport && (
                <GreGenerateReportModal
                    report={greReport.report}
                    kind={greReport.kind}
                    onClose={() => setGreReport(null)}
                />
            )}

            {showGreCompetition && activeLevel === 'GRE' && (
                <GreCompetitionModal
                    onClose={() => setShowGreCompetition(false)}
                    onGenerated={(r) => { setShowGreCompetition(false); setCompetitionResult(r); }}
                />
            )}

            {showSatCompetition && activeLevel === 'SAT' && (
                <SatCompetitionModal
                    onClose={() => setShowSatCompetition(false)}
                    onGenerated={(r) => { setShowSatCompetition(false); setCompetitionResult(r); }}
                />
            )}

            {showBcsFull && activeLevel === 'BCS' && (
                <ModelTestBuilderModal
                    levelName="BCS"
                    distribution={BCS_MODEL_TEST_DISTRIBUTION}
                    baseTotal={BCS_MODEL_TEST_BASE_TOTAL}
                    minutesPerQuestion={BCS_MINUTES_PER_QUESTION}
                    generateUrl={getBcsGeneratePublishedUrl()}
                    onClose={() => setShowBcsFull(false)}
                    onGenerated={(r) => { setShowBcsFull(false); setGreReport({ report: r, kind: 'Published' }); }}
                />
            )}

            {showBcsCompetition && activeLevel === 'BCS' && (
                <CompetitionBuilderModal
                    levelName="BCS"
                    generateUrl={getBcsGenerateCompetitionUrl()}
                    summary={`Draws ${BCS_COMPETITION_TOTAL} questions — an equal share from each of the 10 BCS subjects — timed 60 minutes.`}
                    onClose={() => setShowBcsCompetition(false)}
                    onGenerated={(r) => { setShowBcsCompetition(false); setGreReport({ report: r, kind: 'Competition' }); }}
                />
            )}

            {showMedicalFull && activeLevel === 'MEDICAL' && (
                <ModelTestBuilderModal
                    levelName="Medical"
                    distribution={MEDICAL_MODEL_TEST_DISTRIBUTION}
                    baseTotal={MEDICAL_MODEL_TEST_BASE_TOTAL}
                    minutesPerQuestion={MEDICAL_MINUTES_PER_QUESTION}
                    generateUrl={getMedicalGeneratePublishedUrl()}
                    onClose={() => setShowMedicalFull(false)}
                    onGenerated={(r) => { setShowMedicalFull(false); setGreReport({ report: r, kind: 'Published' }); }}
                />
            )}

            {showMedicalCompetition && activeLevel === 'MEDICAL' && (
                <CompetitionBuilderModal
                    levelName="Medical"
                    generateUrl={getMedicalGenerateCompetitionUrl()}
                    summary="Draws the exam distribution (Biology 30, Chemistry 25, Physics 15, English 15, GK 15 — ~100 questions) timed 75 minutes."
                    onClose={() => setShowMedicalCompetition(false)}
                    onGenerated={(r) => { setShowMedicalCompetition(false); setGreReport({ report: r, kind: 'Competition' }); }}
                />
            )}

            {showDuFull && activeLevel === 'VARSITY' && (
                <ModelTestBuilderModal
                    levelName="DU A-Unit"
                    distribution={DU_MODEL_TEST_DISTRIBUTION}
                    baseTotal={DU_MODEL_TEST_BASE_TOTAL}
                    minutesPerQuestion={DU_MINUTES_PER_QUESTION}
                    generateUrl={getDuGeneratePublishedUrl()}
                    onClose={() => setShowDuFull(false)}
                    onGenerated={(r) => { setShowDuFull(false); setGreReport({ report: r, kind: 'Published' }); }}
                />
            )}

            {showDuCompetition && activeLevel === 'VARSITY' && (
                <CompetitionBuilderModal
                    levelName="DU A-Unit"
                    generateUrl={getDuGenerateCompetitionUrl()}
                    summary={`Draws the 4 science subjects (Physics, Chemistry, Higher Mathematics, Biology — ~${DU_COMPETITION_TOTAL} questions) timed 45 minutes.`}
                    onClose={() => setShowDuCompetition(false)}
                    onGenerated={(r) => { setShowDuCompetition(false); setGreReport({ report: r, kind: 'Competition' }); }}
                />
            )}
        </div>
    );
}

export default AutoQuizBuild;
